import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { companyInfo, socialLinks } from '../data/siteData';
import { useInView } from '../hooks';
import { EMAILJS, isEmailJSReady } from '../config/emailjs';

const WA_NUMBER = '255627857470';

const sendWhatsApp = async (payload) => {
  try {
    await fetch('/api/whatsapp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    // best-effort
  }
};

const ContactPage = () => {
  const [heroRef, heroInView] = useInView(0.1);
  const [formRef, formInView] = useInView(0.1);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    const templateParams = {
      from_name:        form.name,
      from_email:       form.email,
      contact_subject:  form.subject,
      contact_message:  form.message,
      sent_at:          new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' }),
    };

    try {
      if (!isEmailJSReady()) throw new Error('Email service not configured');
      await emailjs.send(EMAILJS.serviceId, EMAILJS.contactTemplate, templateParams, EMAILJS.publicKey);
      await sendWhatsApp({ type: 'contact', ...form });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Could not send your message right now. Please email us at paulnkingwa34@gmail.com');
    }
  };

  const contactCards = [
    {
      icon: '📍',
      label: 'Location',
      value: companyInfo.location,
      sub: 'East Africa',
      color: 'from-primary-500/15 to-primary-600/5 border-primary-500/20',
    },
    {
      icon: '✉️',
      label: 'Email',
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`,
      sub: 'We reply within 24 hours',
      color: 'from-secondary-400/15 to-secondary-600/5 border-secondary-400/20',
    },
    {
      icon: '📞',
      label: 'Phone / WhatsApp',
      value: companyInfo.phone,
      href: `https://wa.me/${WA_NUMBER}`,
      sub: 'Mon – Fri, 9 AM – 6 PM EAT',
      color: 'from-purple-500/15 to-purple-600/5 border-purple-500/20',
    },
  ];

  const socialItems = [
    { name: 'Twitter / X', key: 'twitter', icon: '𝕏', desc: '@rivenbyte' },
    { name: 'Discord', key: 'discord', icon: '💬', desc: 'Join our server' },
    { name: 'Instagram', key: 'instagram', icon: '📸', desc: '@rivenbyte' },
    { name: 'YouTube', key: 'youtube', icon: '▶', desc: 'Dev vlogs & trailers' },
    { name: 'LinkedIn', key: 'linkedin', icon: 'in', desc: 'Riven Byte Studios' },
    { name: 'TikTok', key: 'tiktok', icon: '♪', desc: '@rivenbyte' },
  ];

  const subjects = [
    'General Enquiry',
    'Game Support',
    'Business & Partnership',
    'Press & Media',
    'Careers',
    'Other',
  ];

  const waText = encodeURIComponent(
    `Hi Paul! I sent you a message on the Riven Byte website.\nName: ${form.name}\nEmail: ${form.email}\nSubject: ${form.subject}`
  );

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 md:py-24 relative african-pattern">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black" />
        <div
          ref={heroRef}
          className={`relative z-10 max-w-7xl mx-auto px-6 text-center transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <p className="section-subtitle text-primary-500">WE'D LOVE TO HEAR FROM YOU</p>
          <h1 className="section-title mb-6">
            GET IN <span className="text-gradient">TOUCH</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Whether you're a player, partner, journalist, or just a fan — we're always happy to connect.
          </p>
        </div>
      </section>

      {/* Contact cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 mb-20">
            {contactCards.map((card) => (
              <div
                key={card.label}
                className={`card-hover bg-gradient-to-br ${card.color} border rounded-3xl p-8 text-center`}
              >
                <div className="text-5xl mb-4">{card.icon}</div>
                <h3 className="font-bold text-white text-lg mb-2">{card.label}</h3>
                {card.href ? (
                  <a href={card.href} className="text-gray-300 hover:text-white transition-colors font-medium block mb-1">
                    {card.value}
                  </a>
                ) : (
                  <p className="text-gray-300 font-medium mb-1">{card.value}</p>
                )}
                <p className="text-gray-500 text-sm">{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Form + Social */}
          <div
            ref={formRef}
            className={`grid lg:grid-cols-5 gap-12 transition-all duration-1000 ${formInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white/5 rounded-3xl border border-white/10 p-8 md:p-10">
                {status === 'success' ? (
                  <div className="text-center py-12">
                    <div className="text-7xl mb-6">🎮</div>
                    <h3 className="font-gaming text-2xl text-white mb-3">MESSAGE SENT!</h3>
                    <p className="text-gray-400 mb-2">
                      Thanks for reaching out. We'll get back to you within 24 hours.
                    </p>
                    <p className="text-gray-500 text-sm mb-8">
                      You'll also receive a WhatsApp notification shortly.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <button
                        onClick={() => { setStatus('idle'); setForm({ name: '', email: '', subject: '', message: '' }); }}
                        className="btn-ghost"
                      >
                        Send another message
                      </button>
                      <a
                        href={`https://wa.me/${WA_NUMBER}?text=${waText}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-green-500/15 border border-green-500/30 text-green-400 font-medium hover:bg-green-500/25 transition-colors"
                      >
                        Also message on WhatsApp →
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="font-gaming text-2xl font-bold text-white mb-8">SEND US A MESSAGE</h2>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm text-gray-400 mb-2 font-medium">Your Name *</label>
                          <input
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            required
                            placeholder="John Doe"
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-2 font-medium">Email Address *</label>
                          <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                            placeholder="you@example.com"
                            className="input-field"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-400 mb-2 font-medium">Subject *</label>
                        <select
                          name="subject"
                          value={form.subject}
                          onChange={handleChange}
                          required
                          className="input-field"
                        >
                          <option value="">Select a subject…</option>
                          {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-400 mb-2 font-medium">Message *</label>
                        <textarea
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          required
                          rows={6}
                          placeholder="Tell us what's on your mind…"
                          className="input-field resize-none"
                        />
                      </div>

                      {status === 'error' && (
                        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                          {errorMsg}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={status === 'sending'}
                        className="w-full btn-primary flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {status === 'sending' ? (
                          <>
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending…
                          </>
                        ) : (
                          'Send Message →'
                        )}
                      </button>

                      <p className="text-center text-gray-600 text-xs">
                        We'll reply by email within 24 hours and also respond via WhatsApp.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* Right column */}
            <div className="lg:col-span-2 space-y-8">
              {/* Social links */}
              <div className="bg-white/5 rounded-3xl border border-white/10 p-8">
                <h3 className="font-gaming font-bold text-white mb-6">FIND US ONLINE</h3>
                <div className="grid grid-cols-2 gap-3">
                  {socialItems.map(({ name, key, icon, desc }) => (
                    <a
                      key={key}
                      href={socialLinks[key] || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all group"
                    >
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-500/20 to-secondary-400/20 flex items-center justify-center text-sm font-bold group-hover:scale-110 transition-transform">
                        {icon}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-xs font-semibold truncate">{name}</p>
                        <p className="text-gray-500 text-xs truncate">{desc}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* WhatsApp card */}
              <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 rounded-3xl border border-green-500/20 p-8">
                <h3 className="font-gaming font-bold text-white mb-3">WHATSAPP</h3>
                <p className="text-gray-400 text-sm mb-5">
                  Prefer to chat instantly? Message us directly on WhatsApp.
                </p>
                <a
                  href={`https://wa.me/${WA_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-green-500/20 border border-green-500/30 text-green-300 font-semibold hover:bg-green-500/30 transition-colors w-full justify-center"
                >
                  <span className="text-xl">💬</span>
                  Chat on WhatsApp
                </a>
                <p className="text-gray-600 text-xs mt-3 text-center">+255 627 857 470</p>
              </div>

              {/* Office hours */}
              <div className="bg-white/5 rounded-3xl border border-white/10 p-8">
                <h3 className="font-gaming font-bold text-white mb-5">STUDIO HOURS</h3>
                <div className="space-y-3">
                  {[
                    { day: 'Monday – Friday', hours: '9:00 AM – 6:00 PM' },
                    { day: 'Saturday', hours: '10:00 AM – 2:00 PM' },
                    { day: 'Sunday', hours: 'Closed' },
                  ].map(({ day, hours }) => (
                    <div key={day} className="flex justify-between items-center text-sm border-b border-white/5 pb-3 last:border-0 last:pb-0">
                      <span className="text-gray-400">{day}</span>
                      <span className={hours === 'Closed' ? 'text-gray-600' : 'text-secondary-400 font-medium'}>{hours}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-4">All times in East Africa Time (EAT, UTC+3)</p>
              </div>

              {/* Location */}
              <div className="bg-white/5 rounded-3xl border border-white/10 p-8">
                <h3 className="font-gaming font-bold text-white mb-4">OUR STUDIO</h3>
                <div className="aspect-video rounded-2xl bg-gradient-to-br from-primary-500/10 to-secondary-400/10 border border-white/10 flex items-center justify-center mb-4 overflow-hidden">
                  <svg viewBox="0 0 200 120" className="w-full h-full opacity-60">
                    <defs>
                      <radialGradient id="mapglow"><stop stopColor="#ff6b35" stopOpacity="0.5"/><stop offset="1" stopColor="#ff6b35" stopOpacity="0"/></radialGradient>
                    </defs>
                    <rect width="200" height="120" fill="#0a0a0a"/>
                    <path d="M55,20 Q90,15 130,25 Q165,35 175,60 Q178,85 160,100 Q140,110 110,105 Q80,100 60,85 Q40,70 42,45 Z" fill="#1a2a1a" stroke="#2a4a2a" strokeWidth="1.5" />
                    <circle cx="100" cy="72" r="8" fill="url(#mapglow)" />
                    <circle cx="100" cy="72" r="4" fill="#ff6b35" />
                    <circle cx="100" cy="72" r="2" fill="white" />
                    <text x="100" y="92" textAnchor="middle" fontSize="7" fill="#ff6b35" fontFamily="monospace">Dar es Salaam</text>
                  </svg>
                </div>
                <p className="text-gray-400 text-sm">{companyInfo.address}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
